/**
 * Moving a cursor over what a terminal has already printed, with vim keys.
 *
 * TUIOS has a copy mode and so does tmux, and the reason a browser console needs
 * one is narrower than either: the mouse is often not available. These panes run
 * CLIs that turn on mouse reporting, and when they do, a drag is *theirs* — it
 * selects nothing, it scrolls their list. So the only way to get a line of output
 * out of a pane and into the clipboard can be the keyboard.
 *
 * Deliberately not a second history viewer. The pane already has one — plain text,
 * searchable, selectable — and it exists because these CLIs draw on the alternate
 * screen where nothing scrolls away and there is no scrollback to read. Copy mode
 * is for the screen in front of you: land on the line, take it, leave.
 *
 * The movement is pure and lives here so it can be tested against a buffer rather
 * than against a terminal. Everything that touches xterm stays in the pane.
 */

/** Where the cursor is, in buffer coordinates (absolute row, column). */
export interface CopyCursor {
  row: number
  col: number
}

export interface CopySelection {
  /** Where `v` or `V` was pressed. Null while nothing is being selected. */
  anchor: CopyCursor | null
  /** `V`: whole lines, whatever the columns say. */
  linewise: boolean
}

export interface CopyState extends CopySelection {
  cursor: CopyCursor
}

/** What the buffer looks like to the movement code. */
export interface BufferView {
  /** Absolute row of the first line held. */
  first: number
  /** Absolute row after the last line held. */
  end: number
  /** The text of a row, or '' for one that is not there. */
  line: (row: number) => string
  /** Rows visible at once, for the half-page jumps. */
  rows: number
}

function clampRow(row: number, b: BufferView): number {
  return Math.min(Math.max(row, b.first), Math.max(b.end - 1, b.first))
}

function clampCol(col: number, row: number, b: BufferView): number {
  // One past the last character is not allowed: unlike an editor there is nothing
  // to insert here, and a cursor sitting past the end selects a space that is not
  // in the output.
  return Math.min(Math.max(col, 0), Math.max(b.line(row).replace(/\s+$/, '').length - 1, 0))
}

/** Start of the next word, or the end of the line. */
function nextWord(line: string, col: number): number {
  let i = col
  while (i < line.length && !/\s/.test(line[i] ?? '')) i++
  while (i < line.length && /\s/.test(line[i] ?? '')) i++
  return Math.min(i, Math.max(line.length - 1, 0))
}

/** Start of the word at or before the cursor. */
function prevWord(line: string, col: number): number {
  let i = col - 1
  while (i > 0 && /\s/.test(line[i] ?? '')) i--
  while (i > 0 && !/\s/.test(line[i - 1] ?? '')) i--
  return Math.max(i, 0)
}

/**
 * Applies one key, and says whether it was one of ours.
 *
 * Returns null for a key copy mode does not use, so the caller can decide — and
 * in the pane the caller swallows it rather than passing it to the shell, because
 * a stray `d` reaching a root prompt while somebody thought they were selecting
 * text is exactly what this mode must never do.
 */
export function moveCursor(state: CopyState, key: string, b: BufferView): CopyState | null {
  const { cursor } = state
  const at = (row: number, col: number): CopyState => ({
    ...state,
    cursor: { row: clampRow(row, b), col: clampCol(col, clampRow(row, b), b) },
  })
  const line = b.line(cursor.row)
  const half = Math.max(Math.floor(b.rows / 2), 1)

  switch (key) {
    case 'h':
    case 'ArrowLeft':
      return at(cursor.row, cursor.col - 1)
    case 'l':
    case 'ArrowRight':
      return at(cursor.row, cursor.col + 1)
    case 'j':
    case 'ArrowDown':
      return at(cursor.row + 1, cursor.col)
    case 'k':
    case 'ArrowUp':
      return at(cursor.row - 1, cursor.col)
    case 'w':
      return at(cursor.row, nextWord(line, cursor.col))
    case 'b':
      return at(cursor.row, prevWord(line, cursor.col))
    case '0':
    case 'Home':
      return at(cursor.row, 0)
    case '$':
    case 'End':
      return at(cursor.row, Number.MAX_SAFE_INTEGER)
    case 'g':
      return at(b.first, 0)
    case 'G':
      return at(b.end - 1, 0)
    case 'PageUp':
      return at(cursor.row - b.rows, cursor.col)
    case 'PageDown':
      return at(cursor.row + b.rows, cursor.col)
    case 'ctrl-u':
      return at(cursor.row - half, cursor.col)
    case 'ctrl-d':
      return at(cursor.row + half, cursor.col)
    default:
      return null
  }
}

/** The two ends of the selection, ordered, or null while there is no anchor. */
export function selectionRange(state: CopyState): { from: CopyCursor; to: CopyCursor } | null {
  const { anchor, cursor } = state
  if (!anchor) return null
  const before =
    anchor.row < cursor.row || (anchor.row === cursor.row && anchor.col <= cursor.col)
  return before ? { from: anchor, to: cursor } : { from: cursor, to: anchor }
}

/**
 * The text `y` would put on the clipboard.
 *
 * Trailing spaces go: a terminal pads every line to the full width, so taking
 * them literally would put a line of blanks after each row and make a two-line
 * yank unusable in anything it is pasted into.
 */
export function yankText(state: CopyState, b: BufferView): string {
  const range = selectionRange(state)
  const line = (row: number) => b.line(row).replace(/\s+$/, '')
  if (!range) return line(state.cursor.row)

  const { from, to } = range
  if (state.linewise) {
    const out: string[] = []
    for (let r = from.row; r <= to.row; r++) out.push(line(r))
    return out.join('\n')
  }
  if (from.row === to.row) return line(from.row).slice(from.col, to.col + 1)

  const out: string[] = [line(from.row).slice(from.col)]
  for (let r = from.row + 1; r < to.row; r++) out.push(line(r))
  out.push(line(to.row).slice(0, to.col + 1))
  return out.join('\n')
}
